/*
 * @Author: Alex
 * @Date: 2023-05-10 14:15:56
 * @Description:
 */

import * as ol from 'ol'
import { extend } from '@vmap/core'

export function getMapCanvas(map: ol.Map): HTMLCanvasElement | null {
  if (!map) return null
  let canvas = map
    .getViewport()
    .getElementsByClassName('ol-fixedoverlay')[0] as HTMLCanvasElement
  if (!canvas) {
    if (map.getViewport().querySelector('.ol-layers')) {
      // Add a fixed canvas layer on top of the map
      canvas = document.createElement('canvas')
      canvas.className = 'ol-fixedoverlay'
      map.getViewport().querySelector('.ol-layers')?.after(canvas)
      // Clear before new compose
      map.on('precompose', e => {
        const size = map.getSize()
        if (e.frameState && size) {
          canvas.width = size[0] * e.frameState.pixelRatio
          canvas.height = size[1] * e.frameState.pixelRatio
        }
      })
    } else {
      canvas = map.getViewport().querySelector('canvas') as HTMLCanvasElement
    }
  }
  return canvas as HTMLCanvasElement
}

export class ElementHelper {
  /**
   * 创建一个 element
   * @param tagName html tag
   * @param options {}
   */
  static create(
    tagName: string,
    options?: {
      html?: string | Element | undefined
      text?: string
      className?: string
      parent?: HTMLElement
      options?: any
      on?: any
      change?: any
      click?: any
      [k: string]: any
    }
  ) {
    //
    options = options || {}
    let elt
    // Create text node
    if (tagName === 'TEXT') {
      elt = document.createTextNode((options.html ?? '') as string)
      if (options.parent) options.parent.appendChild(elt)
    } else {
      // Other element
      elt = document.createElement(tagName)
      if (/button/i.test(tagName)) elt.setAttribute('type', 'button')
      for (const attr in options) {
        switch (attr) {
          case 'className': {
            if (options.className && options.className.trim)
              elt.setAttribute('class', options.className.trim())
            break
          }
          case 'text': {
            elt.innerText = options.text ?? ''
            break
          }
          case 'html': {
            if (options.html instanceof Element) elt.appendChild(options.html)
            else if (options.html !== undefined) elt.innerHTML = options.html
            break
          }
          case 'parent': {
            if (options.parent) options.parent.appendChild(elt)
            break
          }
          case 'options': {
            if (/select/i.test(tagName)) {
              for (const i in options.options) {
                ElementHelper.create('OPTION', {
                  html: i,
                  value: options.options[i],
                  parent: elt
                })
              }
            }
            break
          }
          case 'style': {
            ElementHelper.setCssStyle(elt, options.style)
            break
          }
          case 'change':
          case 'click': {
            ElementHelper.addListener(elt, attr, options[attr])
            break
          }
          case 'on': {
            for (const e in options.on) {
              ElementHelper.addListener(elt, e, options.on[e])
            }
            break
          }
          case 'checked': {
            // elt.removeAttribute()
            if (options.checked) {
              elt.setAttribute(attr, '')
            }
            break
          }
          default: {
            elt.setAttribute(attr, options[attr])
            break
          }
        }
      }
    }
    return elt
  }

  static addListener(
    element: HTMLElement | Window,
    eventType: string | string[],
    fn: any,
    useCapture?: any
  ) {
    if (typeof eventType === 'string') eventType = eventType.split(' ')
    eventType.forEach(function (e) {
      element.addEventListener(e, fn, useCapture)
    })
  }

  static removeListener(
    element: HTMLElement | Window,
    eventType: string | string[],
    fn: any
  ) {
    if (typeof eventType === 'string') eventType = eventType.split(' ')
    eventType.forEach(function (e) {
      element.removeEventListener(e, fn)
    })
  }

  /**
   * set css style
   * @param el elemment
   * @param st st
   */
  static setCssStyle(el: HTMLElement, st: { [k: string]: any }) {
    for (const s in st) {
      switch (s) {
        case 'top':
        case 'left':
        case 'bottom':
        case 'right':
        case 'minWidth':
        case 'maxWidth':
        case 'width':
        case 'height': {
          if (typeof st[s] === 'number') {
            el.style[s] = st[s] + 'px'
          } else {
            el.style[s] = st[s]
          }
          break
        }
        default: {
          //   console.log(el.style, s, st[s])
          extend(el.style, { s: st[s] })
          // TESTME:
          //   Reflect.defineProperty(el.style, , st[s])
          //   el.style[s] = st[s]
        }
      }
    }
  }

  /**
   * get style
   * @param el
   * @param styleProp
   * @returns
   */
  static getStyle(el: any, styleProp: string) {
    let value
    const defaultView = (el.ownerDocument || document).defaultView
    // W3C standard way:
    if (defaultView && defaultView.getComputedStyle) {
      // sanitize property name to css notation
      // (hypen separated words eg. font-Size)
      styleProp = styleProp.replace(/([A-Z])/g, '-$1').toLowerCase()
      value = defaultView.getComputedStyle(el, null).getPropertyValue(styleProp)
    } else if (el.currentStyle) {
      // IE
      // sanitize property name to camelCase
      styleProp = styleProp.replace(/-(\w)/g, function (str, letter) {
        return letter.toUpperCase()
      })
      value = el.currentStyle[styleProp]
      // convert other units to pixels on IE
      if (/^\d+(em|pt|%|ex)?$/i.test(value)) {
        return (function (value) {
          const oldLeft = el.style.left,
            oldRsLeft = el.runtimeStyle.left
          el.runtimeStyle.left = el.currentStyle.left
          el.style.left = value || 0
          value = el.style.pixelLeft + 'px'
          el.style.left = oldLeft
          el.runtimeStyle.left = oldRsLeft
          return value
        })(value)
      }
    }
    if (/px$/.test(value)) return parseInt(value)
    return value
  }

  static scrollDiv(elt: HTMLElement, options: any) {
    options = options || {}
    let pos: any = false
    let speed = 0
    let d,
      dt = 0

    const onmove =
      typeof options.onmove === 'function' ? options.onmove : undefined
    //var page = options.vertical ? 'pageY' : 'pageX';
    const page = options.vertical ? 'screenY' : 'screenX'
    const scroll = options.vertical ? 'scrollTop' : 'scrollLeft'
    let moving: any = false
    // Factor scale content / container
    let scale: number, isbar: boolean
    // Initialize scroll container for minibar
    let scrollContainer: undefined | HTMLElement
    let scrollbar: undefined | HTMLElement

    // Update the minibar
    let updateCounter = 0
    const updateMinibar = function () {
      if (scrollbar) {
        updateCounter++
        setTimeout(updateMinibarDelay)
      }
    }
    const updateMinibarDelay = function () {
      if (scrollbar) {
        updateCounter--
        // Prevent multi call
        if (updateCounter) return
        // Container height
        const pheight = elt.clientHeight
        // Content height
        const height = elt.scrollHeight
        // Set scrollbar value
        scale = pheight / height
        scrollbar.style.height = scale * 100 + '%'
        scrollbar.style.top = (elt.scrollTop / height) * 100 + '%'
        if (scrollContainer) {
          scrollContainer.style.height = pheight + 'px'
          // No scroll
          if (pheight > height - 0.5) scrollContainer.classList.add('ol-100pc')
          else scrollContainer.classList.remove('ol-100pc')
        }
      }
    }

    // Handle pointer down
    const onPointerDown = (e: any) => {
      // Prevent scroll
      if ((e.target as HTMLElement).classList.contains('ol-noscroll')) return
      // Start scrolling
      moving = false
      pos = e[page]
      dt = new Date().getTime()
      elt.classList.add('ol-move')
      // Prevent elt dragging
      e.preventDefault()
      // Listen scroll
      window.addEventListener('pointermove', onPointerMove)
      ElementHelper.addListener(
        window,
        ['pointerup', 'pointercancel'],
        onPointerUp
      )
    }

    // Register scroll
    const onPointerMove = function (e: any) {
      if (pos !== false) {
        const delta = (isbar ? -1 / scale : 1) * (pos - e[page])
        moving = moving || Math.round(delta)
        elt[scroll] += delta
        d = new Date().getTime()
        if (d - dt) {
          speed = (speed + delta / (d - dt)) / 2
        }
        pos = e[page]
        dt = d
        // Tell we are moving
        if (delta) onmove(true)
      } else {
        moving = true
      }
    }

    // Animate scroll
    const animate = function (to: number) {
      const step = to > 0 ? Math.min(100, to / 2) : Math.max(-100, to / 2)
      to -= step
      elt[scroll] += step
      if (-1 < to && to < 1) {
        if (moving)
          setTimeout(function () {
            elt.classList.remove('ol-move')
          })
        else elt.classList.remove('ol-move')
        moving = false
        onmove(false)
      } else {
        setTimeout(function () {
          animate(to)
        }, 40)
      }
    }

    if (options.vertical && options.minibar) {
      const init = function (b: any) {
        // only once
        elt.removeEventListener('pointermove', init)
        // if (elt.parentNode) {
        if (elt.parentElement) {
          elt.parentElement.classList.add('ol-miniscroll')
        }
        scrollbar = ElementHelper.create('DIV') as HTMLElement
        scrollContainer = ElementHelper.create('DIV', {
          className: 'ol-scroll',
          html: scrollbar
        }) as HTMLElement
        elt.parentNode?.insertBefore(scrollContainer, elt)
        // Move scrollbar
        scrollbar.addEventListener('pointerdown', function (e) {
          isbar = true
          onPointerDown(e)
        })
        // Handle mousewheel
        if (options.mousewheel) {
          ElementHelper.addListener(
            scrollContainer,
            ['mousewheel', 'DOMMouseScroll', 'onmousewheel'],
            function (e: any) {
              onMouseWheel(e)
            }
          )
          ElementHelper.addListener(
            scrollbar,
            ['mousewheel', 'DOMMouseScroll', 'onmousewheel'],
            function (e: any) {
              onMouseWheel(e)
            }
          )
        }
        // Update on enter
        elt.parentNode?.addEventListener('pointerenter', updateMinibar)
        // Update on resize
        window.addEventListener('resize', updateMinibar)
        // Update
        if (b !== false) updateMinibar()
      }
      // Allready inserted in the DOM
      if (elt.parentNode) init(false)
      // or wait when ready
      else elt.addEventListener('pointermove', init)
      // Update on scroll
      elt.addEventListener('scroll', function () {
        updateMinibar()
      })
    }

    // Enable scroll
    elt.style.touchAction = 'none'
    elt.style.overflow = 'hidden'
    elt.classList.add('ol-scrolldiv')

    // Start scrolling
    ElementHelper.addListener(elt, ['pointerdown'], function (e: any) {
      isbar = false
      onPointerDown(e)
    })

    // Prevet click when moving...
    elt.addEventListener(
      'click',
      function (e) {
        if (elt.classList.contains('ol-move')) {
          e.preventDefault()
          e.stopPropagation()
        }
      },
      true
    )

    // Stop scrolling
    const onPointerUp = function (e: any) {
      dt = new Date().getTime() - dt
      if (dt > 100 || isbar) {
        // User stop: no speed
        speed = 0
      } else if (dt > 0) {
        // Calculate new speed
        speed = ((speed || 0) + (pos - e[page]) / dt) / 2
      }
      animate(options.animate === false ? 0 : speed * 200)
      pos = false
      speed = 0
      dt = 0
      // Add class to handle click (on iframe / double-click)
      if (!elt.classList.contains('ol-move')) {
        elt.classList.add('ol-hasClick')
        setTimeout(function () {
          elt.classList.remove('ol-hasClick')
        }, 500)
      } else {
        elt.classList.remove('ol-hasClick')
      }
      isbar = false
      window.removeEventListener('pointermove', onPointerMove)
      ElementHelper.removeListener(
        window,
        ['pointerup', 'pointercancel'],
        onPointerUp
      )
    }

    // Handle mousewheel
    const onMouseWheel = function (e: any) {
      const delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail))
      elt.classList.add('ol-move')
      elt[scroll] -= delta * 30
      elt.classList.remove('ol-move')
      return false
    }
    if (options.mousewheel) {
      // && !elt.classList.contains('ol-touch')) {
      ElementHelper.addListener(
        elt,
        ['mousewheel', 'DOMMouseScroll', 'onmousewheel'],
        onMouseWheel
      )
    }

    return {
      refresh: updateMinibar
    }
  }
}
